import { useState } from "react";
import Reveal from "@/components/feature/Reveal";
import { commentItems, hotTopics } from "@/mocks/homeComments";
import { tagClass } from "@/pages/home/tagStyles";

export default function CommentSection() {
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");
  const [likedIds, setLikedIds] = useState<string[]>([]);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = () => {
    if (!draft.trim()) {
      setNotice("先说点什么吧，写清楚你发现的细节更容易被核对。");
      return;
    }
    setNotice("已记录你的核对意见（当前为演示，不会真实提交）。");
    setDraft("");
  };

  return (
    <section id="discuss" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
        <Reveal>
          <div className="mb-6 overflow-hidden rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <span className="text-xs font-medium tracking-widest text-accent-800">
                  DISCUSSION
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  讨论区 · 谁的细节对得上
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-700">
                  材料摆出来了，接下来靠大家一起核。把你发现的时间戳、顺序、字段差异写在评论里，比其他地方更容易对上。
                </p>
              </div>
              <div className="flex shrink-0 gap-6">
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-700">1,842</p>
                  <p className="mt-1 text-xs text-foreground-600">累计评论</p>
                </div>
                <div className="border-l border-accent-200 pl-6">
                  <p className="font-heading text-2xl font-bold text-accent-800">176</p>
                  <p className="mt-1 text-xs text-foreground-600">今日新增</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="说说你核对到的细节，比如时间戳、顺序差异、字段对不上的位置……"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-xs text-foreground-500">
                    {draft.length}/500 · 请尽量写清材料编号与时间点
                  </span>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    发表评论
                  </button>
                </div>
                {notice && (
                  <p className="mt-3 rounded-md bg-background-200 px-3 py-2 text-xs leading-relaxed text-foreground-700">
                    {notice}
                  </p>
                )}
              </div>
            </Reveal>

            <div className="mt-4 flex flex-col gap-4">
              {commentItems.map((comment, index) => {
                const liked = likedIds.includes(comment.id);
                return (
                  <Reveal key={comment.id} delay={index * 60}>
                    <article className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                          {comment.avatar}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground-900">
                              {comment.name}
                            </span>
                            {comment.badge && (
                              <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">
                                {comment.badge}
                              </span>
                            )}
                            <span className="text-xs text-foreground-400">{comment.time}</span>
                          </div>
                          <p className="mt-2.5 text-sm leading-relaxed text-foreground-700">
                            {comment.content}
                          </p>
                          <div className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-2">
                            <button
                              type="button"
                              onClick={() => toggleLike(comment.id)}
                              className={`inline-flex cursor-pointer items-center gap-1.5 text-xs transition-colors ${
                                liked ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                              }`}
                            >
                              <i
                                className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}
                              ></i>
                              {comment.likes + (liked ? 1 : 0)}
                            </button>
                            <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                              <i className="ri-chat-3-line"></i>
                              {comment.replies}
                            </span>
                            <span
                              className={`rounded-md px-2 py-0.5 text-xs font-medium ${tagClass(
                                comment.tag
                              )}`}
                            >
                              {comment.tag}
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <Reveal className="lg:col-span-4" delay={80}>
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-line text-accent-600"></i>
                正在热议
              </h3>
              <ul className="mt-4 flex flex-col">
                {hotTopics.map((topic, index) => (
                  <li
                    key={topic.id}
                    className={`flex items-center gap-3 py-3 ${
                      index !== 0 ? "border-t border-background-200" : ""
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                        index < 3
                          ? "bg-primary-100 text-primary-700"
                          : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="flex-1 truncate text-sm text-foreground-800">
                      {topic.name}
                    </span>
                    <span className="shrink-0 text-xs text-foreground-400">
                      {topic.replies} 回复
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                查看全部话题
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}